import type { PrimitivePresentationOperation, RuntimeState } from '@/adapters/primitive/_contract/config';
import type { PrimitiveOperationInputDraft } from '@/adapters/primitive/_contract/operation';

export function createPrimitiveOperationInputDraft(
    operation: PrimitivePresentationOperation,
): PrimitiveOperationInputDraft {
    switch (operation) {
        case 'addition':
        case 'subtraction':
        case 'multiplication':
        case 'division':
            return { operation, operandOneText: '', operandTwoText: '' };
        case 'natural-identity':
            return { operation, inputText: '' };
        case 'boolean-identity':
            return { operation, inputText: '' };
        case 'logical-not':
            return { operation, inputText: '' };
        case 'logical-and':
        case 'logical-nand':
        case 'logical-xor':
        case 'logical-xnor':
        case 'logical-or':
        case 'logical-nor':
            return { operation, operandOneText: '', operandTwoText: '' };
        case 'natural-less-than':
            return { operation, sourceText: '', targetText: '' };
    }
}

export function createInitialPrimitiveRuntimeState(
    operation: PrimitivePresentationOperation = 'addition',
): RuntimeState {
    return {
        operation,
        inputDraft: createPrimitiveOperationInputDraft(operation),
        committedStepIndex: 0,
        pendingStepIndex: null,
        playbackAdvanceMode: 'manual',
        isPlaying: false,
    };
}

export function requestNextPrimitiveStep(
    runtimeState: RuntimeState,
    maxStepIndex: number,
): RuntimeState {
    if (runtimeState.pendingStepIndex !== null) return runtimeState;
    if (runtimeState.committedStepIndex >= maxStepIndex) return runtimeState;
    return {
        ...runtimeState,
        pendingStepIndex: runtimeState.committedStepIndex + 1,
    };
}

export function retreatPrimitiveStep(runtimeState: RuntimeState): RuntimeState {
    return {
        ...runtimeState,
        committedStepIndex: Math.max(0, runtimeState.committedStepIndex - 1),
        inputEditing: false,
        pendingStepIndex: null,
        playbackAdvanceMode: 'manual',
        isPlaying: false,
    };
}

/** Animates the traversed steps backwards; completing the rewind settles there without resuming forward playback. */
export function requestPrimitiveRewind(runtimeState: RuntimeState, targetStepIndex: number): RuntimeState {
    if (runtimeState.pendingStepIndex !== null || runtimeState.booleanAssignment !== undefined
        || !Number.isInteger(targetStepIndex) || targetStepIndex < 0
        || targetStepIndex >= runtimeState.committedStepIndex) return runtimeState;
    return {
        ...runtimeState,
        pendingStepIndex: targetStepIndex,
        inputEditing: false,
        playbackAdvanceMode: 'manual',
        isPlaying: false,
    };
}

export function togglePrimitivePlayback(
    runtimeState: RuntimeState,
    maxStepIndex: number,
): RuntimeState {
    if (runtimeState.isPlaying) {
        return {
            ...runtimeState,
            isPlaying: false,
        };
    }

    if (runtimeState.committedStepIndex >= maxStepIndex && runtimeState.pendingStepIndex === null) return runtimeState;
    return requestNextPrimitiveStep({
        ...runtimeState,
        playbackAdvanceMode: 'automatic',
        isPlaying: true,
    }, maxStepIndex);
}

export function completePrimitiveTransition(
    runtimeState: RuntimeState,
    maxStepIndex: number,
): RuntimeState {
    if (runtimeState.pendingStepIndex === null) return runtimeState;

    const committedStepIndex = runtimeState.pendingStepIndex;
    if (committedStepIndex < runtimeState.committedStepIndex) {
        return { ...runtimeState, committedStepIndex, pendingStepIndex: null };
    }
    const shouldContinuePlayback = runtimeState.playbackAdvanceMode === 'automatic' && runtimeState.isPlaying
        && committedStepIndex < maxStepIndex;

    return {
        ...runtimeState,
        committedStepIndex,
        pendingStepIndex: shouldContinuePlayback ? committedStepIndex + 1 : null,
        isPlaying: committedStepIndex < maxStepIndex && runtimeState.isPlaying,
    };
}